import {
  createEffect,
  createSignal,
  For,
  onCleanup,
  Show,
  type JSX,
} from 'solid-js';
import { isServer } from 'solid-js/web';
import SecureImage from '@components/ui/SecureImage';

// Strain-review image gallery. Owns the e-commerce-style hero +
// thumbnail layout on desktop, a swipeable snap-carousel on mobile,
// and a fullscreen lightbox shared by both.
//
// Persistence model: the parent passes raw `private:<path>` strings
// fetched from the database. Every <img> goes through SecureImage,
// which signs each path on demand against the private_images bucket
// — so the gallery doesn't need an authenticated Supabase reference
// itself, and SSR-rendered HTML degrades gracefully (the placeholder
// box paints before the signed URLs land).
//
// Reactivity rules:
//   * All controls carry `type="button"` so a parent <form> never
//     submits when a thumbnail or arrow is clicked.
//   * <For> walks props.images directly (Solid memoises by reference;
//     since the parent is expected to pass a stable array fetched
//     once per review, the rows aren't re-keyed on every render).
//   * The mobile-carousel ref is captured with the ref-callback
//     pattern matching the rest of the codebase (no JSX-side ref
//     thunks).
//   * Body-scroll lock + keyboard listeners are scoped to the
//     `isLightboxOpen()` reactive expression — the effect cleans up
//     its listener every time the lightbox closes, so we never leak
//     a global keydown handler when the user navigates away.

type Props = {
  /** Raw `private:<path>` references stored on the review row. */
  images: string[];
};

export default function ReviewImageGallery(props: Props): JSX.Element {
  const [activeIndex, setActiveIndex] = createSignal(0);
  const [isLightboxOpen, setIsLightboxOpen] = createSignal(false);
  let carouselRef: HTMLDivElement | undefined;

  const count = () => props.images.length;
  const hasMultiple = () => count() > 1;
  const safeIndex = () => Math.min(activeIndex(), Math.max(count() - 1, 0));

  // Re-clamp activeIndex if the parent swaps to a shorter array
  // (e.g. an admin removed an image while the gallery was mounted).
  createEffect(() => {
    if (activeIndex() >= count() && count() > 0) {
      setActiveIndex(count() - 1);
    }
  });

  function selectIndex(i: number) {
    setActiveIndex(i);
    // Mobile path: keep the carousel's visible page in lockstep with
    // the model index so dot-clicks and arrow-key navigation in the
    // lightbox both move the underlying carousel into position. The
    // scroll triggers our onScroll handler too, but the no-op write
    // ('idx === activeIndex') makes that safe.
    if (carouselRef) {
      carouselRef.scrollTo({
        left: i * carouselRef.clientWidth,
        behavior: 'smooth',
      });
    }
  }

  function goPrev() {
    if (count() === 0) return;
    selectIndex((safeIndex() - 1 + count()) % count());
  }

  function goNext() {
    if (count() === 0) return;
    selectIndex((safeIndex() + 1) % count());
  }

  function openLightboxAt(i: number) {
    setActiveIndex(i);
    setIsLightboxOpen(true);
  }

  function onCarouselScroll(e: Event & { currentTarget: HTMLDivElement }) {
    const el = e.currentTarget;
    if (el.clientWidth === 0) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    if (idx !== activeIndex()) setActiveIndex(idx);
  }

  // Body scroll lock + keyboard navigation while the lightbox is
  // open. Effect re-runs when isLightboxOpen() flips so cleanup is
  // automatic — `onCleanup` registered inside an effect run is torn
  // down before the next run (or on unmount).
  createEffect(() => {
    if (isServer) return;
    if (!isLightboxOpen()) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setIsLightboxOpen(false);
      } else if (e.key === 'ArrowRight') {
        goNext();
      } else if (e.key === 'ArrowLeft') {
        goPrev();
      }
    }
    window.addEventListener('keydown', onKey);

    onCleanup(() => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    });
  });

  return (
    <Show when={count() > 0}>
      <div class="w-full">
        {/* ------------------------------------------------------------------
            Desktop view — hero on top, thumbnail strip below.
            ------------------------------------------------------------------ */}
        <div class="hidden md:block">
          <button
            type="button"
            onClick={() => openLightboxAt(safeIndex())}
            class="block w-full overflow-hidden rounded-xl border border-brand-hex bg-brand-hex/20 cursor-zoom-in"
            aria-label="Open image fullscreen"
          >
            <SecureImage
              src={props.images[safeIndex()] ?? ''}
              alt={`Review image ${safeIndex() + 1} of ${count()}`}
              class="w-full max-h-[28rem] object-contain bg-brand-hex/10"
            />
          </button>

          <Show when={hasMultiple()}>
            <ul class="mt-3 grid grid-cols-5 gap-2" role="tablist" aria-label="Review images">
              <For each={props.images}>
                {(src, i) => {
                  const isActive = () => i() === safeIndex();
                  return (
                    <li>
                      <button
                        type="button"
                        onClick={() => selectIndex(i())}
                        role="tab"
                        aria-selected={isActive()}
                        aria-label={`Show image ${i() + 1}`}
                        class={`block w-full overflow-hidden rounded-md border-2 transition-colors cursor-pointer ${
                          isActive()
                            ? 'border-brand-green'
                            : 'border-transparent hover:border-brand-hex'
                        }`}
                      >
                        <SecureImage
                          src={src}
                          alt={`Thumbnail ${i() + 1}`}
                          class="w-full aspect-square object-cover bg-brand-hex/20"
                        />
                      </button>
                    </li>
                  );
                }}
              </For>
            </ul>
          </Show>
        </div>

        {/* ------------------------------------------------------------------
            Mobile view — snap-x carousel + pagination dots.
            ------------------------------------------------------------------ */}
        <div class="block md:hidden">
          <div
            ref={(el) => (carouselRef = el)}
            onScroll={onCarouselScroll}
            class="flex w-full snap-x snap-mandatory overflow-x-auto rounded-xl border border-brand-hex bg-brand-hex/20"
            // Hide the scrollbar on mobile webkit while keeping the
            // native swipe gesture intact. The native bar on iOS is
            // already off-by-default; this is mainly Android Chrome.
            style={{ 'scrollbar-width': 'none' }}
            aria-roledescription="carousel"
            aria-label="Review images"
          >
            <For each={props.images}>
              {(src, i) => (
                <button
                  type="button"
                  onClick={() => openLightboxAt(i())}
                  class="block min-w-full shrink-0 snap-center cursor-zoom-in"
                  aria-label={`Open image ${i() + 1} fullscreen`}
                >
                  <SecureImage
                    src={src}
                    alt={`Review image ${i() + 1} of ${count()}`}
                    class="w-full max-h-[24rem] object-contain"
                  />
                </button>
              )}
            </For>
          </div>

          <Show when={hasMultiple()}>
            <div
              class="mt-2 flex items-center justify-center gap-1.5"
              role="tablist"
              aria-label="Image pagination"
            >
              <For each={props.images}>
                {(_, i) => {
                  const isActive = () => i() === safeIndex();
                  return (
                    <button
                      type="button"
                      onClick={() => selectIndex(i())}
                      role="tab"
                      aria-selected={isActive()}
                      aria-label={`Go to image ${i() + 1}`}
                      class={`h-2 rounded-full transition-all cursor-pointer ${
                        isActive()
                          ? 'w-4 bg-brand-green'
                          : 'w-2 bg-brand-hex hover:bg-brand-moss/60'
                      }`}
                    />
                  );
                }}
              </For>
            </div>
          </Show>
        </div>

        {/* ------------------------------------------------------------------
            Lightbox — fullscreen overlay shared by desktop + mobile.
            ------------------------------------------------------------------ */}
        <Show when={isLightboxOpen()}>
          <div
            class="fixed inset-0 z-50 flex items-center justify-center bg-black/95"
            role="dialog"
            aria-modal="true"
            aria-label="Image viewer"
            onClick={(e) => {
              // Close on backdrop click but NOT when the click
              // originates inside the image or controls.
              if (e.target === e.currentTarget) {
                setIsLightboxOpen(false);
              }
            }}
          >
            {/* Close — top right. */}
            <button
              type="button"
              onClick={() => setIsLightboxOpen(false)}
              class="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white text-xl hover:bg-white/20 cursor-pointer focus:outline-none focus:ring-2 focus:ring-white"
              aria-label="Close image viewer"
            >
              ×
            </button>

            {/* Prev / next — only when there's somewhere to go. */}
            <Show when={hasMultiple()}>
              <button
                type="button"
                onClick={goPrev}
                class="absolute left-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white text-2xl hover:bg-white/20 cursor-pointer focus:outline-none focus:ring-2 focus:ring-white"
                aria-label="Previous image"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={goNext}
                class="absolute right-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white text-2xl hover:bg-white/20 cursor-pointer focus:outline-none focus:ring-2 focus:ring-white"
                aria-label="Next image"
              >
                ›
              </button>

              {/* Counter — bottom centre. Helps the viewer know how
                  many images are still ahead without counting dots. */}
              <div class="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-xs text-white">
                {safeIndex() + 1} / {count()}
              </div>
            </Show>

            <SecureImage
              src={props.images[safeIndex()] ?? ''}
              alt={`Review image ${safeIndex() + 1} of ${count()}`}
              class="max-h-screen max-w-screen object-contain"
            />
          </div>
        </Show>
      </div>
    </Show>
  );
}
